/**
 * Ownership — the pure half. No React, no fetch.
 *
 * ===========================================================================
 * THREE STATES, THREE SENTENCES, AND NONE OF THEM IS THE OTHERS
 * ===========================================================================
 * The screen this replaces rendered ownership as
 *
 *     {finding.ownerUserId ? 'Assigned' : 'Nobody has taken this on'}
 *
 * which is the fold this programme has now found in seven backend places and
 * twelve frontend ones. "Nobody has taken this on" was shown for a finding a
 * named ROLE was accountable for, and it was shown as a settled fact for one
 * nobody had decided about yet. Two different mistakes from one nullable id.
 *
 * So every sentence about ownership is built HERE, from the discriminated
 * union the server sends, in a function with a case per arm — which means a
 * new arm is a compile error rather than a silently missing sentence, and a
 * test can hold each sentence separately.
 *
 * ===========================================================================
 * AND A SUGGESTION IS NEVER WORDED AS AN ANSWER
 * ===========================================================================
 * `suggestionSentence` says what the server FOUND and where it looked. It never
 * says "the owner is", never says "Phibian thinks", and never implies pressing
 * the button confirms something already decided — because nothing has been.
 */

import type {
  FindingOwner,
  FindingOwnerSuggestion,
  FindingOwnerSuggestionNote,
} from './insightsClient.js';

/**
 * WHAT THIS FINDING'S OWNERSHIP SAYS. One sentence, and never a blank.
 *
 * `tone` exists so the screen can style "nobody has decided" and "owned by a
 * role with nobody in it" as things to look at, without either of them
 * borrowing the other's words.
 */
export interface OwnerSentence {
  readonly headline: string;
  readonly detail: string | null;
  readonly tone: 'settled' | 'open' | 'attention';
}

export function ownerSentence(owner: FindingOwner): OwnerSentence {
  switch (owner.kind) {
    case 'unresolved':
      // NOT "nobody has taken this on", which reads as a settled fact about
      // the finding. Nobody has DECIDED — the decision is still owed.
      return {
        headline: 'Nobody has decided who owns this',
        detail:
          'This is an open question, not a finding that was meant to be unowned. Assign a person or a responsible role.',
        tone: 'open',
      };
    case 'user':
      switch (owner.disclosure) {
        case 'shown':
          return { headline: owner.label, detail: 'is accountable for this.', tone: 'settled' };
        case 'withheld':
          // SOMEBODY OWNS THIS. The reader may not be told who, and that is a
          // different fact from there being nobody — `explain/owner.ts` says
          // why at length and the sentence has to carry the difference.
          return {
            headline: 'Somebody owns this',
            detail: 'A named person is accountable. You are not shown who.',
            tone: 'settled',
          };
        case 'absent':
          return {
            headline: 'The person who owned this is no longer in the directory',
            detail:
              'The assignment stands in the record; the account it names has no directory entry any more. Somebody should decide again.',
            tone: 'attention',
          };
      }
    // eslint-disable-next-line no-fallthrough -- every disclosure returns above
    case 'role': {
      const name = owner.roleName ?? owner.roleKey;
      if (name === null) {
        return {
          headline: 'A role owns this, and this organization cannot see it',
          detail:
            'The finding names a responsible role that is not one of your roles or a system role. Somebody should decide again.',
          tone: 'attention',
        };
      }
      if (owner.resolvesToday === 0) {
        // OWNED AND UNSTAFFED — a third thing, and not "unowned". This is the
        // failure 1760400000000 was written about: a queue that resolves to
        // nobody, forever, with nobody told.
        return {
          headline: `${name} owns this, and nobody holds that role here today`,
          detail:
            'The role is accountable and it currently resolves to no one whose access covers this finding. That is not the same as unowned, and it will not fix itself.',
          tone: 'attention',
        };
      }
      return {
        headline: `${name} owns this`,
        detail:
          owner.resolvesToday === 1
            ? 'One person holds that role here today.'
            : `${owner.resolvesToday} people hold that role here today.`,
        tone: 'settled',
      };
    }
  }
}

/** The short form, for a list cell. Still three states; still never a blank. */
export function ownerCellLabel(owner: FindingOwner): string {
  switch (owner.kind) {
    case 'unresolved':
      return 'Not decided';
    case 'user':
      switch (owner.disclosure) {
        case 'shown':
          return owner.label;
        case 'withheld':
          return 'Owned · not shown to you';
        case 'absent':
          return 'Owner no longer in directory';
      }
    // eslint-disable-next-line no-fallthrough -- every disclosure returns above
    case 'role': {
      const name = owner.roleName ?? owner.roleKey ?? 'A role';
      return owner.resolvesToday === 0 ? `${name} · nobody holds it` : `${name} (role)`;
    }
  }
}

/**
 * WHAT THE SERVER FOUND, WORDED AS A FINDING AND NOT AS A DECISION.
 *
 * Every sentence names the RELATION it came from. None of them says "we
 * recommend", "Phibian suggests" or "the owner is": a reader must be able to
 * see that this is a record the organization already holds, so that accepting
 * it is their judgement about a fact rather than deference to an opinion.
 */
export function suggestionSentence(suggestion: FindingOwnerSuggestion): string {
  switch (suggestion.source) {
    case 'program_lead':
      return `${suggestion.displayName} is recorded as the lead of this finding’s program.`;
    case 'role_holder': {
      const holders =
        suggestion.resolvesToday === 1
          ? '1 person holds it'
          : `${suggestion.resolvesToday} people hold it`;
      return `${suggestion.roleName} is a role in this organization; ${holders} with access covering this finding.`;
    }
  }
}

/** The caveat every suggestion carries, because none of these relations is dated. */
export const UNDATED_SUGGESTION_NOTE =
  'These come from records this organization already holds, read as they stand today. None of them is dated, so none can say who was responsible at the time this was noticed.';

/**
 * WHY THERE IS NOTHING TO OFFER — which is four different answers, and an empty
 * list that said nothing would be read as the first one.
 */
export function consultedSentence(note: FindingOwnerSuggestionNote): string | null {
  if (note.outcome === 'offered') return null;
  switch (note.source) {
    case 'program_lead':
      switch (note.outcome) {
        case 'not_applicable':
          return 'This finding is not inside a program, so there is no program lead to look up.';
        case 'absent':
          return 'This finding’s program has no lead recorded, or the lead has no account to assign work to.';
        case 'withheld':
          return 'This finding’s program has a lead. You are not shown who they are.';
      }
    // eslint-disable-next-line no-fallthrough -- every outcome returns above
    case 'role_holder':
      switch (note.outcome) {
        case 'absent':
          return 'No role in this organization currently has a holder whose access covers this finding.';
        case 'withheld':
          return 'The roles in this organization are not shown to you.';
        case 'not_applicable':
          return 'Roles were not consulted for this finding.';
      }
  }
}
